import {THEME} from '@wandb/weave/common/css/color.styles';
import React from 'react';
import {AbstractSeries, ScaleUtils} from 'react-vis';

import {getMousePosition} from '../../util/mouse';
import {isMobile} from '../../util/panelbankFlow';
import {
  type BrushZoomIntent,
  getLinePlotBrushZoomPercentages,
  type LinePlotZoomDragMetadata,
} from './brushZoomMetadata';

export type {BrushZoomIntent} from './brushZoomMetadata';

type DrawArea = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export type DomainArea = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  classes: string;
};

export type BrushEndMetadata = {
  interrupt?: boolean;
} & LinePlotZoomDragMetadata;

export type BrushEndHandler = (
  area: DomainArea | null,
  zoomIntent: BrushZoomIntent,
  metadata: BrushEndMetadata
) => void;

export const DRAG_THRESHOLD_X = 5;
export const DRAG_THRESHOLD_Y = 25;
export const EMPTY_BRUSH_ZOOM_INTENT: BrushZoomIntent = {
  shouldZoomX: false,
  shouldZoomY: false,
};

export function getDragZoomIntent(
  xPixels: number,
  yPixels: number
): BrushZoomIntent {
  return {
    shouldZoomX: Math.abs(xPixels) >= DRAG_THRESHOLD_X,
    shouldZoomY: Math.abs(yPixels) >= DRAG_THRESHOLD_Y,
  };
}

function clampToRange(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

export function getDrawAreaAndZoomIntent({
  drawArea,
  innerHeight,
  innerWidth,
  locX,
  locY,
  startLocX,
  startLocY,
}: {
  drawArea: DrawArea;
  innerHeight: number;
  innerWidth: number;
  locX: number;
  locY: number;
  startLocX: number;
  startLocY: number;
}): {drawArea: DrawArea; zoomIntent: BrushZoomIntent} {
  const clampedLocX = clampToRange(locX, 0, innerWidth);
  const clampedLocY = clampToRange(locY, 0, innerHeight);
  // Raw pointer travel determines intent so edge drags can intentionally zoom
  // into tiny non-zero ranges. Clamped dimensions only suppress zero-area ranges.
  const rawZoomIntent = getDragZoomIntent(locX - startLocX, locY - startLocY);
  const selectedWidth = Math.abs(clampedLocX - startLocX);
  const selectedHeight = Math.abs(clampedLocY - startLocY);
  const zoomIntent = {
    shouldZoomX: rawZoomIntent.shouldZoomX && selectedWidth > 0,
    shouldZoomY: rawZoomIntent.shouldZoomY && selectedHeight > 0,
  };

  const left = Math.min(startLocX, clampedLocX);
  const right = Math.max(startLocX, clampedLocX);

  if (zoomIntent.shouldZoomY) {
    return {
      drawArea: {
        ...drawArea,
        top: Math.min(startLocY, clampedLocY),
        bottom: Math.max(startLocY, clampedLocY),
        left,
        right,
      },
      zoomIntent,
    };
  }

  return {
    drawArea: {
      ...drawArea,
      left,
      right,
      top: 0,
      bottom: innerHeight,
    },
    zoomIntent,
  };
}

function getMouseOffsetInContainer(
  event: React.MouseEvent,
  marginLeft: number,
  marginTop: number
): {offsetX: number; offsetY: number} {
  const mousePosition = getMousePosition(event);
  return {
    offsetX: mousePosition.offset.x - marginLeft,
    offsetY: mousePosition.offset.y - marginTop,
  };
}

export default class Highlight extends AbstractSeries<any> {
  static displayName = 'HighlightOverlay';
  static defaultProps = {
    allow: 'x',
    color: THEME.textPrimary,
    opacity: 0.1,
  };

  state = {
    drawing: false,
    drawArea: {top: 0, right: 0, bottom: 0, left: 0},
    startLocX: 0,
    startLocY: 0,
    initialXDomain: null,
    disableZoom: isMobile(),
    zoomIntent: EMPTY_BRUSH_ZOOM_INTENT,
  };

  updateDrawing = (drawing: boolean) => {
    this.props.setIsDrawing(drawing);
    this.setState({drawing});
  };

  wrapperRef = React.createRef();

  // Coalesces hover mousemove handling to one update per animation frame.
  // Chrome delivers raw (uncoalesced) input events while DevTools is open
  // (the inspector Overlay agent requests unbuffered input), so dispatching
  // highlight updates directly from every mousemove can starve rendering.
  highlightFrame: number | null = null;
  latestHighlightXY: {x: number; y: number} | null = null;

  scheduleMouseMoveWithXY(mouseX: number, mouseY: number) {
    this.latestHighlightXY = {x: mouseX, y: mouseY};
    if (this.highlightFrame != null) {
      return;
    }
    this.highlightFrame = window.requestAnimationFrame(() => {
      this.highlightFrame = null;
      if (this.latestHighlightXY != null) {
        this.props.onMouseMoveWithXY(
          this.latestHighlightXY.x,
          this.latestHighlightXY.y
        );
      }
    });
  }

  cancelScheduledMouseMove() {
    if (this.highlightFrame != null) {
      window.cancelAnimationFrame(this.highlightFrame);
      this.highlightFrame = null;
    }
    this.latestHighlightXY = null;
  }

  onParentMouseLeave = () => {
    this.cancelScheduledMouseMove();
  };

  _getDrawArea(locX: number, locY: number) {
    const {innerWidth, innerHeight} = this.props;
    const {drawArea, startLocX, startLocY} = this.state;

    return getDrawAreaAndZoomIntent({
      drawArea,
      innerHeight,
      innerWidth,
      locX,
      locY,
      startLocX,
      startLocY,
    });
  }

  getMouseXInContainer(event: React.MouseEvent) {
    return getMousePosition(event).offset.x - this.props.marginLeft;
  }

  getMouseYInContainer(event: React.MouseEvent) {
    return getMousePosition(event).offset.y - this.props.marginTop;
  }

  onParentMouseDown = (e: React.MouseEvent) => {
    const {innerHeight, onBrushStart} = this.props;

    const {offsetX, offsetY} = getMouseOffsetInContainer(
      e,
      this.props.marginLeft,
      this.props.marginTop
    );
    this.updateDrawing(true);
    this.setState({
      drawArea: {
        top: 0,
        right: offsetX,
        bottom: innerHeight,
        left: offsetX,
      },
      startLocX: offsetX,
      startLocY: offsetY,
      zoomIntent: EMPTY_BRUSH_ZOOM_INTENT,
    });

    if (onBrushStart) {
      onBrushStart(e);
    }
  };

  /**
   * The interrupt functionality is added so that if a user is pressing the cmd key to trigger opening a run in a new window we won't trigger a zoom click which would zoom them out if they're zoomed.
   */
  stopDrawing = (shouldInterrupt: boolean = false) => {
    // Quickly short-circuit if the user isn't drawing in our component
    if (!this.state.drawing) {
      return;
    }

    const {onBrushEnd, onMouseUp} = this.props;
    const {drawArea, disableZoom, zoomIntent} = this.state as any;
    const xScale = ScaleUtils.getAttributeScale(this.props as any, 'x');
    const yScale = ScaleUtils.getAttributeScale(this.props as any, 'y');
    const classes = ' zoomed-in';
    const brushEndMetadata: BrushEndMetadata = {
      interrupt: shouldInterrupt,
      ...getLinePlotBrushZoomPercentages({
        drawArea,
        innerHeight: this.props.innerHeight,
        innerWidth: this.props.innerWidth,
        zoomIntent,
      }),
    };

    this.updateDrawing(false);
    // Clear the draw area
    this.setState({
      drawArea: {top: 0, right: 0, bottom: 0, left: 0},
      startLocX: 0,
      startLocY: 0,
      zoomIntent: EMPTY_BRUSH_ZOOM_INTENT,
    });

    if (!zoomIntent.shouldZoomX && !zoomIntent.shouldZoomY) {
      this.setState({disableZoom: isMobile()});
      onBrushEnd(null, zoomIntent, brushEndMetadata);
      onMouseUp();
      return;
    }

    // Compute the corresponding domain drawn
    const domainArea: DomainArea = {
      top: (yScale as any).invert(drawArea.top),
      right: (xScale as any).invert(drawArea.right),
      bottom: (yScale as any).invert(drawArea.bottom),
      left: (xScale as any).invert(drawArea.left),
      classes,
    };

    if (onBrushEnd && !disableZoom) {
      onBrushEnd(domainArea, zoomIntent, brushEndMetadata);
    }
    onMouseUp();
  };

  onParentMouseMove = (e: React.MouseEvent) => {
    const {onBrush} = this.props;
    const {drawing} = this.state;

    e.preventDefault();

    // this is true if you're hovering over a datapoint, which screws up the offset calculation.
    // ignoring these mouseovers fixes this bug: https://github.com/wandb/core/issues/2429
    if ((e.nativeEvent.target as any).nodeName === 'circle') {
      return;
    }

    if (!this.wrapperRef.current) {
      return;
    }
    const {offsetX, offsetY} = getMouseOffsetInContainer(
      e,
      this.props.marginLeft,
      this.props.marginTop
    );

    if (drawing) {
      const {drawArea: newDrawArea, zoomIntent} = this._getDrawArea(
        offsetX,
        offsetY
      );
      this.setState({drawArea: newDrawArea, zoomIntent});
      if (onBrush) {
        onBrush(e);
      }
    } else {
      const xScale = ScaleUtils.getAttributeScale(this.props as any, 'x');
      const mouseX = (xScale as any).invert(offsetX);
      const yScale = ScaleUtils.getAttributeScale(this.props as any, 'y');
      const mouseY = (yScale as any).invert(offsetY);
      this.scheduleMouseMoveWithXY(mouseX, mouseY);
    }
  };

  handleMouseUp = () => {
    if (this.state.drawing) {
      this.stopDrawing();
    }
  };

  componentDidMount() {
    window.addEventListener('mouseup', this.handleMouseUp, false);
  }

  componentWillUnmount() {
    window.removeEventListener('mouseup', this.handleMouseUp, false);
    this.cancelScheduledMouseMove();
  }

  onParentMouseUp = (e: React.MouseEvent | React.TouchEvent) => {
    this.stopDrawing('metaKey' in e ? e.metaKey : false);
  };

  onTouchCancel = () => {
    this.stopDrawing();
  };

  render() {
    const {marginLeft, marginTop, innerWidth, innerHeight, color, opacity} =
      this.props;
    const {
      drawArea: {left, right, top, bottom},
      disableZoom,
      drawing,
      zoomIntent,
    } = this.state as any;
    const highlightLeft = zoomIntent.shouldZoomX ? left : 0;
    const highlightRight = zoomIntent.shouldZoomX ? right : innerWidth;
    const highlightTop = zoomIntent.shouldZoomY ? top : 0;
    const highlightBottom = zoomIntent.shouldZoomY ? bottom : innerHeight;

    return (
      <g
        ref={this.wrapperRef as any}
        transform={`translate(${marginLeft}, ${marginTop})`}
        className="highlight-container"
        onTouchEnd={this.onParentMouseUp}
        onTouchCancel={this.onTouchCancel}>
        <rect
          className="mouse-target"
          fill="black"
          opacity="0"
          x={0}
          y={0}
          // prevent console error for negative value
          width={Math.max(innerWidth, 0)}
          height={Math.max(innerHeight, 0)}
        />
        {drawing && (zoomIntent.shouldZoomX || zoomIntent.shouldZoomY) && (
          <>
            <rect
              className="highlight"
              pointerEvents="none"
              opacity={!disableZoom ? opacity : 0}
              fill={color}
              x={highlightLeft}
              y={highlightTop}
              width={highlightRight - highlightLeft}
              height={highlightBottom - highlightTop}
            />
          </>
        )}
      </g>
    );
  }
}
